class FullscreenSearch {
  constructor() {
    this.overlay = null;
    this.input = null;
    this.results = null;
    this.isOpen = false;
    this.searchTimeout = null;
    this.init();
  }

  init() {
    this.createOverlay();
    this.bindEvents();
  }

  createOverlay() {
    // Create the fullscreen search overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'fullscreen-search-overlay';
    this.overlay.innerHTML = `
      <div class="fullscreen-search-container">
        <div class="fullscreen-search-header">
          <h2 class="fullscreen-search-title">Search</h2>
          <button type="button" class="fullscreen-search-close" aria-label="Close search">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6L18 18"/>
            </svg>
          </button>
        </div>
        
        <form class="fullscreen-search-form" action="/search" method="get" role="search">
          <div class="fullscreen-search-input-wrapper">
            <input
              type="search"
              name="q"
              class="fullscreen-search-input"
              placeholder="Search products..."
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
            >
            <button type="submit" class="fullscreen-search-submit" aria-label="Search">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"/>
              </svg>
            </button>
          </div>
        </form>
        
        <div class="fullscreen-search-results" style="display: none;"></div>
      </div>
    `;

    // Get references to elements
    this.input = this.overlay.querySelector('.fullscreen-search-input');
    this.results = this.overlay.querySelector('.fullscreen-search-results');
    this.form = this.overlay.querySelector('.fullscreen-search-form');
    this.closeButton = this.overlay.querySelector('.fullscreen-search-close');

    // Add to body
    document.body.appendChild(this.overlay);
  }

  bindEvents() {
    // Close button
    this.closeButton.addEventListener('click', () => this.close());

    // Form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Input changes for live search
    this.input.addEventListener('input', (e) => this.handleInput(e));

    // Keyboard events
    this.overlay.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Click outside to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Global escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  open() {
    this.isOpen = true;
    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Focus input after animation
    setTimeout(() => {
      this.input.focus();
    }, 300);
  }

  close() {
    this.isOpen = false;
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';
    this.clearResults();
    this.input.value = '';
  }

  handleSubmit(e) {
    const query = this.input.value.trim();
    if (!query) {
      e.preventDefault();
      this.input.focus();
      return;
    }

    // Let the form submit naturally
    this.close();
  }

  handleInput(e) {
    const query = e.target.value.trim();

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Clear results if query is too short
    if (query.length < 3) {
      this.clearResults();
      return;
    }

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performLiveSearch(query);
    }, 300);
  }

  async performLiveSearch(query) {
    if (!query || query.length < 3) return;

    this.showLoading();

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`
      );

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const data = await response.json();
      const products = data.resources?.results?.products || [];

      this.displayResults(products, query);
    } catch (error) {
      console.error('Search error:', error);
      this.showError();
    }
  }

  showLoading() {
    this.results.style.display = 'block';
    this.results.innerHTML = `
      <div class="fullscreen-search-loading">
        <div class="loading-spinner">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="31.416" stroke-dashoffset="31.416">
              <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
              <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
            </circle>
          </svg>
          <p>Searching...</p>
        </div>
      </div>
    `;
  }

  displayResults(products, query) {
    this.results.style.display = 'block';

    if (!products || products.length === 0) {
      this.results.innerHTML = `
        <div class="fullscreen-search-no-results">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21L16.514 16.506"/>
          </svg>
          <h3>No products found</h3>
          <p>Try adjusting your search terms for "${query}"</p>
        </div>
      `;
      return;
    }

    const resultsHTML = products
      .map((product, index) => `
        <a href="${product.url}" class="fullscreen-search-result-item" style="animation-delay: ${index * 0.1}s">
    <img 
  src="${product.featured_image?.url || '/assets/placeholder.png'}" 
  alt="${product.title}" 
  class="fullscreen-search-result-image"
  loading="lazy"
>
          <div class="fullscreen-search-result-details">
            <h3 class="fullscreen-search-result-title">${product.title}</h3>
            <p class="fullscreen-search-result-price">${this.formatPrice(product.price)}</p>
          </div>
        </a>
      `)
      .join('');

    this.results.innerHTML = resultsHTML;
  }

  showError() {
    this.results.style.display = 'block';
    this.results.innerHTML = `
      <div class="fullscreen-search-no-results">
        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <h3>Search Error</h3>
        <p>Unable to perform search. Please try again.</p>
      </div>
    `;
  }

  clearResults() {
    this.results.style.display = 'none';
    this.results.innerHTML = '';
  }

  handleKeydown(e) {
    // Handle arrow key navigation through results
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const resultItems = this.results.querySelectorAll('.fullscreen-search-result-item');
      const currentIndex = Array.from(resultItems).findIndex(item => item === document.activeElement);

      if (resultItems.length === 0) return;

      let nextIndex;
      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < resultItems.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : resultItems.length - 1;
      }

      resultItems[nextIndex]?.focus();
    }
  }

  formatPrice(price) {
    // Basic price formatting - you might want to use Shopify's money format
    if (!price) return '';

    const numericPrice = parseFloat(price);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(numericPrice);
  }
}

// Initialize fullscreen search
document.addEventListener('DOMContentLoaded', () => {
  window.fullscreenSearch = new FullscreenSearch();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FullscreenSearch;
} 